/**
 * Back-fills the flat `$exception_*` properties on autocaptured browser
 * exceptions.
 *
 * `posthog-js` has two exception paths and they do not agree on shape:
 *
 * - `captureException()` — used by `lib/posthog-logging-service.ts` via
 *   `logger.error` — sets `$exception_type` and `$exception_message`
 *   explicitly.
 * - **exception autocapture** (`capture_exceptions: true`), which catches
 *   unhandled crashes, emits only `$exception_list` and `$exception_level`.
 *   The flat properties are never set.
 *
 * The dashboard and the infra panel filter on the flat properties, so every
 * autocaptured crash reads as `None` for type and message — which is exactly
 * what the `/diary` `$exception` events showed. Nothing was broken about the
 * capture; the two paths simply produce different property sets.
 *
 * This normalises them at the `before_send` boundary so both look the same in
 * PostHog. It never overwrites a value the SDK already set.
 */

/** One entry of `$exception_list`, as the autocapture extension builds it. */
interface ExceptionListEntry {
  type?: string
  value?: string
}

interface ExceptionProperties {
  $exception_list?: unknown
  $exception_type?: unknown
  $exception_message?: unknown
  [key: string]: unknown
}

/** Reads the first entry of `$exception_list`, which describes the thrown error. */
function firstException(properties: ExceptionProperties): ExceptionListEntry | null {
  const list = properties.$exception_list
  if (!Array.isArray(list) || list.length === 0) return null
  const first: unknown = list[0]
  if (!first || typeof first !== 'object') return null
  return first as ExceptionListEntry
}

/**
 * Returns `properties` with `$exception_type` / `$exception_message` filled in
 * from `$exception_list` where they are missing.
 *
 * Mutates nothing and never overwrites: a value the SDK set is always more
 * authoritative than one derived here.
 */
export function backfillExceptionProperties(properties: ExceptionProperties): ExceptionProperties {
  const hasType = typeof properties.$exception_type === 'string' && properties.$exception_type
  const hasMessage = typeof properties.$exception_message === 'string' && properties.$exception_message
  if (hasType && hasMessage) return properties

  const first = firstException(properties)
  if (!first) return properties

  const backfilled: ExceptionProperties = { ...properties }
  if (!hasType && typeof first.type === 'string' && first.type) {
    backfilled.$exception_type = first.type
  }
  if (!hasMessage && typeof first.value === 'string' && first.value) {
    backfilled.$exception_message = first.value
  }
  return backfilled
}

/** Minimal shape of what `before_send` receives, so this stays SDK-free. */
export interface CapturedEvent {
  event: string
  properties?: ExceptionProperties
}

/**
 * `before_send` hook. Normalises `$exception` events and passes everything else
 * through untouched.
 *
 * Returning `null` drops an event, so a `null` in must stay a `null` out — a
 * previous hook in the chain has already decided to drop it.
 */
export function normalizeExceptionEvent<T extends CapturedEvent>(event: T | null): T | null {
  if (!event || event.event !== '$exception' || !event.properties) return event
  return { ...event, properties: backfillExceptionProperties(event.properties) }
}
